import React, { useEffect, useState } from 'react';
import '../styles/components/patient-portal-selector.scss';
import FmhConnectionPrompt from './FmhConnectionPrompt';
import RetiredPracticeMessage from './RetiredPracticeMessage';
import { isRetiredWithoutFmh } from '../helpers/practiceRetirement';
const CARET_ICON = '/assets/images/caret-right.svg';

const PatientPortalSelector = ({
  authData,
  hashState,
  isLoadingPatients,
  onSetSpinner,
  onTransitionToPhr,
  patients,
  patientAccounts,
  patientError,
  practices,
}) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showPatientAccountSelector, setShowPatientAccountSelector] = useState(
    false
  );
  const [shouldDisplayBackButton, setShouldDisplayBackButton] = useState(
    false
  );

  useEffect(
    () => {
      onSetSpinner(false);

      // Determine if we need to show account selector
      if (patientAccounts.length === 1) {
        setSelectedAccount(patientAccounts[0]);
      } else {
        setSelectedAccount(null);
      }
      setShowPatientAccountSelector(patientAccounts.length > 1);
      setShouldDisplayBackButton(patientAccounts.length > 1);
    },
    [patientAccounts, onSetSpinner]
  );

  const handlePatientAccountClick = (account) => {
    setSelectedAccount(account);
    setShowPatientAccountSelector(false);
  };

  const goToPatientFusion = () => {
    if (authData && authData.redirectUrl) {
      onSetSpinner(true);
      onTransitionToPhr(authData.redirectUrl, null);
    } else {
      console.error('No redirect URL found');
    }
  };

  const onGoBackToAccountSelector = () => {
    setSelectedAccount(null);
    setShowPatientAccountSelector(true);
  };

  // If we have a selected account, determine what to show
  if (selectedAccount) {
    const correspondingPractice = selectedAccount.practice;
    const isFmhOnboarded =
      correspondingPractice &&
      correspondingPractice.is_fmh_onboarding_completed;

    if (isRetiredWithoutFmh(correspondingPractice)) {
      return (
        <div className="max-height">
          <RetiredPracticeMessage
            onGoBackToAccountSelector={onGoBackToAccountSelector}
            onSetSpinner={onSetSpinner}
            practiceName={selectedAccount.practiceName}
            shouldDisplayBackButton={shouldDisplayBackButton}
          />
        </div>
      );
    }

    if (isFmhOnboarded) {
      return (
        <div className="max-height">
          <FmhConnectionPrompt
            account={selectedAccount}
            onSetSpinner={onSetSpinner}
            practices={[correspondingPractice]}
            onGoBackToAccountSelector={onGoBackToAccountSelector}
            shouldDisplayBackButton={shouldDisplayBackButton}
          />
        </div>
      );
    } else {
      const redirectUrl = (authData && authData.redirectUrl) ? authData.redirectUrl : null;
      onTransitionToPhr(redirectUrl, null);
      return null; // Component will unmount after redirect
    }
  }

  // Show patient records list if multiple records available (selector phase)
  if (showPatientAccountSelector) {
    return (
      <div className="patient-portal-selector">
        <div className="patient-portal-selector__container">
          <h1 className="patient-portal-selector__title">
            Which account would you like to use?
          </h1>

          <div className="patient-portal-selector__records">
            {patientAccounts.length > 0 ? (
              patientAccounts.map((account, index) => (
                <button
                  key={index}
                  className="patient-portal-selector__record-item"
                  onClick={() => handlePatientAccountClick(account)}
                >
                  <div className="patient-portal-selector__record-item-content">
                    <span className="patient-portal-selector__record-name">
                      {account.fullName}
                    </span>
                    <span className="patient-portal-selector__record-practice-name">
                      {account.practiceName}
                    </span>
                  </div>
                  <div className="patient-portal-selector__record-item-caret">
                    <img
                      src={CARET_ICON}
                      alt="Caret Icon Directed Right"
                      className="patient-portal-selector__record-item-caret-icon"
                    />
                  </div>
                </button>
              ))
            ) : (
              <div className="patient-portal-selector__no-practices">
                <p>No eligible patient accounts found.</p>
                <button
                  className="patient-portal-selector__select-button"
                  onClick={() => goToPatientFusion()}
                >
                  Go to Patient Fusion
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PatientPortalSelector;
